import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SharedModule } from '@Shared/shared.module';
import { areDatesEquivalent } from '@Utils/time-utils';

interface Unavailability {
  from: Date;
  to: Date;
  user: string;
  isAvailable: boolean;
}

@Component({
  selector: 'app-team-calendar',
  templateUrl: './team-calendar.component.html',
  styleUrls: ['./team-calendar.component.scss'],
  imports: [SharedModule]
})
export class TeamCalendarComponent implements OnInit, OnDestroy {
  @Input() teamMembers!: FormArray;
  @Input() selectedDay: Date;
  @Input() setSelectedDay!: (day: Date) => void;

  currentMonth: number;
  currentYear: number;
  weeks: (Date | null)[][] = [];
  unavailabilityList: Unavailability[] = [];

  private scheduleSub: Subscription | null = null;

  ngOnInit(): void {
    this.currentMonth = this.selectedDay?.getMonth();
    this.currentYear = this.selectedDay?.getFullYear();
    this.generateCalendar();

    // Setup valueChanges watcher
    this.scheduleSub = this.teamMembers.valueChanges.subscribe(() => {
      this.extractUnavailability();
    });

    // Initial extraction
    this.extractUnavailability();
  }

  ngOnDestroy(): void {
    this.scheduleSub?.unsubscribe();
  }

  extractUnavailability(): void {
    this.unavailabilityList = []; // clear existing list
    for (const ctrl of this.teamMembers.controls) {
      const ctrlValue = (ctrl as FormGroup);
      const schedules = ctrlValue.get('schedules').value;
      const isAvailable = ctrlValue.get('userAvailable').value;
      const user = ctrlValue.get('userId').value;
      schedules.map(schedule => {
        const from = new Date(schedule['formattedStartTime']);
        const to = new Date(schedule['formattedEndTime']);
        this.unavailabilityList.push({ from, to, user, isAvailable });
      });
    };
  }

  generateCalendar(): void {
    const firstDayOfMonth = new Date(this.currentYear, this.currentMonth, 1);
    const lastDayOfMonth = new Date(this.currentYear, this.currentMonth + 1, 0);

    const startDay = new Date(firstDayOfMonth);
    startDay.setDate(startDay.getUTCDate() - startDay.getUTCDay());

    const endDay = new Date(lastDayOfMonth);
    endDay.setDate(endDay.getUTCDate() + (6 - endDay.getUTCDay()));

    const current = new Date(startDay);
    const weeks: (Date | null)[][] = [];

    while (current <= endDay) {
      const week: (Date | null)[] = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(current));
        current.setDate(current.getUTCDate() + 1);
      }
      weeks.push(week);
    }

    this.weeks = weeks;
  }

  isSameDay(d1: Date, d2: Date): boolean {
    return d1.getFullYear() === d2.getFullYear() &&
          d1.getMonth() === d2.getMonth() &&
          d1.getDate() === d2.getDate();
  }

  isToday(d: Date): boolean {
    const today = new Date();
    return d.getFullYear() === today.getFullYear() &&
           d.getMonth() === today.getMonth() &&
           d.getDate() === today.getDate();
  }

  getToggledOffUsersCount(userIds: Set<string>): number {
    let count = 0;
    for (const ctrl of this.teamMembers.controls) {
      const ctrlValue = (ctrl as FormGroup);
      const userId = ctrlValue.get('userId').value;
      if (ctrlValue.get('userAvailable').value === false && !userIds.has(userId)) {
        count += 1;
      }
    };
    return count;
  }

  getUnavailabilityForDay(day: Date): number {
    // DEV NOTE: modify this for debugging purposes/limiting the logic to specific dates
    // if (day.getMonth() != 10 || day.getDate() != 7) {
    //   return 0;
    // }
    const target = new Date(day);
    const today = new Date();
    
    if (this.isSameDay(target, today)) {
      const filteredUnavailabiltyList = this.unavailabilityList.filter(range => {
        // for today's date DON'T count team members who are currently available (regardless of future schedules)
        return !range.isAvailable && !areDatesEquivalent(target, range.to) && (today >= range.from && today <= range.to)
      });

      const userIdsHelper = new Set(filteredUnavailabiltyList.map(u => u.user) as [string]);
      const count = this.getToggledOffUsersCount(userIdsHelper);
      return userIdsHelper.size + count;
    } 
    else if (target > today) {
      const filteredUnavailabiltyList = this.unavailabilityList?.filter(range => {
        // for future dates we only care if the day matches, or falls within a multi-day range
        return !areDatesEquivalent(target, range.to) && (this.isSameDay(target, range.from) || (target >= range.from && target <= range.to))
      });

      const userIdsHelper = new Set(filteredUnavailabiltyList.map(u => u.user) as [string]);
      const count = this.getToggledOffUsersCount(userIdsHelper);
      return userIdsHelper.size + count;
    }
    return 0;
  }

  selectCalendarDay(day: Date) {
    const currentDate = new Date();
    const dayMatchesCurrentDate = day.getFullYear() === currentDate.getFullYear() && day.getMonth() === currentDate.getMonth() && day.getDate() === currentDate.getDate();
    if ((!dayMatchesCurrentDate && day < currentDate))
      return;
    this.setSelectedDay(day);
  }

  prevMonth(): void {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth(): void {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }

  getMonthYearLabel(): string {
    const date = new Date(this.currentYear, this.currentMonth);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  }
}
